import React, { useCallback, useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ImageUploadEdit = ({ mainImage, additionalImages, onMainImageChange, onAdditionalImagesChange }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleMainImageDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) onMainImageChange(file);
  }, [onMainImageChange]);

  const handleAdditionalImagesDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    onAdditionalImagesChange([...additionalImages, ...files]);
  }, [additionalImages, onAdditionalImagesChange]);

  const removeAdditionalImage = (index) => {
    const newImages = [...additionalImages];
    newImages.splice(index, 1);
    onAdditionalImagesChange(newImages);
  };

  const dropZoneClass = `mt-1 flex justify-center rounded-lg border-2 ${
    isDragging ? 'border-orange-500 bg-orange-50' : 'border-dashed border-gray-900/25'
  } px-6 py-10`;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Main Product Image</label>
        <div
          className={dropZoneClass}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleMainImageDrop}
        >
          <div className="text-center">
            {mainImage ? (
              <div className="relative inline-block">
                <img loading="lazy" 
                  src={typeof mainImage === 'string' ? mainImage : URL.createObjectURL(mainImage)}
                  alt="Main product"
                  className="h-40 w-40 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => onMainImageChange(null)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" />
                <div className="flex text-sm leading-6 text-gray-600 justify-center">
                  <label className="relative cursor-pointer rounded-md bg-white font-semibold text-orange-600">
                    <span>Upload main image</span>
                    <input
                      type="file"
                      className="sr-only"
                      onChange={(e) => e.target.files[0] && onMainImageChange(e.target.files[0])}
                      accept="image/*"
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">or drag and drop</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Additional Product Images</label>
        <div
          className={dropZoneClass}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleAdditionalImagesDrop}
        >
          <div className="text-center">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              {additionalImages.map((img, index) => (
                <div key={index} className="relative inline-block">
                  <img loading="lazy" 
                    src={typeof img === 'string' ? img : URL.createObjectURL(img)}
                    alt={`Product ${index + 2}`}
                    className="h-32 w-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeAdditionalImage(index)}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" />
              <div className="flex text-sm leading-6 text-gray-600 justify-center">
                <label className="relative cursor-pointer rounded-md bg-white font-semibold text-orange-600">
                  <span>Upload additional images</span>
                  <input
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={(e) => onAdditionalImagesChange([...additionalImages, ...Array.from(e.target.files)])}
                    accept="image/*"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500">or drag and drop</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadEdit;
